import {
  Pagination,
  Table,
  TableColumns,
  useBulkSelection,
  useTableHelpers,
  useTablePagination,
} from '@teamsnap/snap-ui';
import { ActionColumn } from 'components/common/action-column';
import { FilterSearchBar } from 'components/common/filterSearchBar/FilterSearchBar';
import { BulkAction } from 'components/common/table/paginated-table';
import { reactiveFeedbackMessage } from 'core/apollo-cache';
import { currencyGivenPennies } from 'core/utils';
import { AMPLITUDE_REGISTRATION_ACTIONS, useAmplitude } from 'hooks/amplitude';
import { LoadingType } from 'hooks/registrationParticipants';
import {
  useRegistrationParticipants,
  useRegistrationParticipantsActions,
} from 'hooks/registrationParticipants/useRegistrationParticipants';
import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { EmailType } from '../SendEmailModal';
import { FieldType, FormResult } from '../types';
import {
  formatColumns,
  formatDateFields,
  formatFilters,
  getActionsForParticipant,
  handleStoredDocumentsForRow,
} from './helpers';
import { ParticipantListModals } from './ParticipantListModals';
import './registration-participants-list.scss';
import { getBulkActions } from './rowActions';
import { ModalType, ParticipantListModal } from './types';

const itemsPerPage = 50;

const modalClosed: ParticipantListModal = { isOpen: false, modalType: null };

interface Props {
  orgId: string;
  formId: string;
  fields: FieldType[];
}

type Sorting = {
  key: string;
  direction: 'asc' | 'desc';
};

const initialSortKey = 'submittedAt';

export const RegistrationParticipantList = ({
  orgId,
  formId,
  fields,
}: Props) => {
  // TODO: Remove these state variables when implementing Snap-UI Table
  const [sorting, setSorting] = useState<Sorting>({
    direction: 'asc',
    key: initialSortKey,
  });

  const [parsedData, setParsedData] = useState<FormResult[]>([]);

  // TODO: Consolidate when refactoring modal logic into a hook
  const [modal, setModal] = useState<ParticipantListModal>(modalClosed);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [selectedFormResult, setSelectedFormResult] =
    useState<FormResult>(null);
  const [totalRegistrants, setTotalRegistrants] = useState<number | null>(null);
  const [exportModalTitle, setExportModalTitle] =
    React.useState('Generate Export');
  const [emailType, setEmailType] = useState<EmailType>('');

  const { amplitudeTrack } = useAmplitude();
  const {
    formResults,
    filters,
    columns,
    activeFilters,
    selectedColumns,
    searchTerm,
    loading,
  } = useRegistrationParticipants();
  const {
    getFormResults,
    getColumns,
    onSelectColumns,
    getUniqueRegistrationsCount,
    getFilters,
    updateSearchTerm,
    updateActiveFilters,
  } = useRegistrationParticipantsActions();

  // Fetch columns and filters from backend
  useEffect(() => {
    (async () => {
      try {
        await getColumns(+orgId, +formId);
        await getFilters(+orgId, +formId);
      } catch (e) {
        console.error(e);
        reactiveFeedbackMessage({
          message:
            'There was an error loading the registration participant list. Please try again.',
          isError: true,
          autoclose: true,
        });
      }
    })();
  }, [orgId, formId]);

  useEffect(() => {
    if (!formResults?.rows || !columns?.length) {
      return;
    }
    setParsedData(
      formResults?.rows?.map((x) => ({
        ...handleStoredDocumentsForRow(columns, x),
        ...formatDateFields(x, fields),
        id: x.formResultId,
        orderBalance: incompleteStatus(x.status)
          ? '--'
          : currencyGivenPennies(Number(x.orderBalance)),
        orderNumber: incompleteStatus(x.status) ? '--' : x.orderNumber,
      })) || []
    );
  }, [formResults, columns, fields]);

  const incompleteStatus = (status) =>
    status === 'In Cart' || status === 'In Progress';

  const handleAmplitudeAction = (
    registration_action: AMPLITUDE_REGISTRATION_ACTIONS
  ) => {
    amplitudeTrack.tsbRegistrationActionClicked({
      registration_action,
    });
  };

  // TODO: Update/remove when refactoring table to snap-ui component
  const whenSelectRecords = (selected: { id: string }[]) => {
    if (selected.length === 0) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(selected);
    }
  };

  const handleExportSelected = () => {
    setExportModalTitle(
      `Export options for ${selectedRecords?.length} selected participant${
        selectedRecords?.length > 1 ? 's' : ''
      }`
    );
    setModal({ isOpen: true, modalType: ModalType.EXPORT });
  };

  const handleEmailAll = async () => {
    setTotalRegistrants(
      await getUniqueRegistrationsCount(
        +orgId,
        +formId,
        searchTerm,
        formatFilters(activeFilters)
      )
    );
    setModal({ isOpen: true, modalType: ModalType.EMAIL });
  };

  const clearFormResult = () => {
    setSelectedFormResult(null);
  };

  const resetEmailStates = () => {
    setModal(modalClosed);
    setTotalRegistrants(null);
    setEmailType('');
    setSelectedFormResult(null);
  };

  const bulkActions: BulkAction[] = getBulkActions({
    disabled: !selectedRecords?.length,
    handleAmplitudeAction,
    clearFormResult,
    setModal,
    handleExportSelected,
    handleEmailAll,
    setEmailType,
  });

  const totalItems = formResults?.pageInfo?.totalEntries || 0;

  const displayColumns: TableColumns = formatColumns(
    columns.filter((col) => selectedColumns.includes(col.name))
  );
  displayColumns['actions'] = {
    label: '',
    align: 'center',
    getBodyProps: (formResult: FormResult) => ({
      type: 'custom',
      children: (
        <ActionColumn
          actions={getActionsForParticipant(
            formResult,
            setSelectedFormResult,
            setModal,
            orgId,
            setEmailType,
            handleAmplitudeAction
          )}
          testId={'registration-list-actions-component'}
        />
      ),
    }),
  };

  const { rows, headerRow } = useTableHelpers({
    data: parsedData,
    columns: displayColumns,
    initialSorting: {
      sortKey: initialSortKey,
      method: 'alpha',
      acsending: true,
    },
    onSort: (sortKey, ascending) => {
      setSorting({ direction: ascending ? 'asc' : 'desc', key: sortKey });
    },
  });

  const { currentPage, onPageChange, pageSize } = useTablePagination({
    rows,
    pageSize: itemsPerPage,
    totalRowCount: totalItems,
  });

  const {
    rows: selectableRows,
    toggleSelectAll,
    allRowsSelected,
  } = useBulkSelection({
    rows,
    onChangeSelected: whenSelectRecords,
  });

  const fetchFormResults = useCallback(() => {
    const params = {
      pageNumber: currentPage,
      pageSize: itemsPerPage,
      searchTerm,
      sortOrder: sorting.direction,
      sortBy: sorting.key,
      filters: formatFilters(activeFilters),
    };
    getFormResults(+orgId, +formId, params);
  }, [
    orgId,
    formId,
    currentPage,
    searchTerm,
    sorting,
    JSON.stringify(activeFilters),
  ]);

  // Fetch form results whenever params are changed
  useEffect(() => {
    fetchFormResults();
  }, [fetchFormResults]);

  const selectedRows = selectableRows.filter((row) => row.selected);

  return (
    <div className="u-padBottomXxl registration-participant-list">
      <FilterSearchBar
        filters={filters}
        columns={columns || []}
        searchTerm={searchTerm}
        placeholder="Search by name, email, or reg ID"
        activeFilters={activeFilters}
        selectedRows={selectedRows}
        isDefaultExpanded
        setActiveFilters={updateActiveFilters}
        setSelectedColumns={onSelectColumns}
        onExport={() => {
          setExportModalTitle('Generate Export');
          setModal({ isOpen: true, modalType: ModalType.EXPORT });
        }}
        selectedColumns={selectedColumns || []}
        bulkActions={bulkActions}
        isExportButtonDisabled={selectableRows.length > 0}
        onSearch={(searchTerm: string) => {
          updateSearchTerm(searchTerm);
        }}
      />
      <Table
        data-testid="registration-participants-table"
        rows={selectableRows}
        loading={
          loading[LoadingType.GetFormResults] || loading[LoadingType.GetColumns]
        }
        className="sui-w-full sui-overflow-x-scroll sui-pt-2"
        headerRow={headerRow}
        toggleSelectAll={toggleSelectAll}
        stickyLastColumn
        allRowsSelected={allRowsSelected}
      />
      <Pagination
        className="sui-mt-3"
        {...{
          currentPage,
          onPageChange,
          totalCount: totalItems,
          pageSize,
        }}
      />
      <ParticipantListModals
        orgId={orgId}
        formId={formId}
        modalProps={modal}
        emailType={emailType}
        resetEmailStates={resetEmailStates}
        totalRegistrants={totalRegistrants}
        onClose={() => setModal(modalClosed)}
        selectedFormResult={selectedFormResult}
        setSelectedFormResult={setSelectedFormResult}
        refetchFormResults={() => {
          fetchFormResults();
        }}
        setSelectedRecords={setSelectedRecords}
        selectedRecords={selectedRecords}
        exportModalTitle={exportModalTitle}
        exportModalColumns={columns}
        sortBy={sorting.key}
        sortOrder={sorting.direction}
      />
    </div>
  );
};
